import { useEffect, useRef } from 'react'

import { useIsEditMode } from '@/hooks/useIsEditMode'

import { useDisclosure } from '@chakra-ui/react'

import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import { selectMe } from '@/redux/me/meSlice'
import { selectConfirmEditMode, toggleEditMode } from '@/redux/ui/uiSlice'
import { trackEvent } from '@/utils/analytics'

import { FLOOR_PLAN_EDIT_MODE_ANALYTICS } from '../constants/constants'

interface UseEditModeToggleIProps {
  /**
   * Resolved by the page's floor-details query, so it is `undefined` for the
   * first renders — the `entered` event waits for it rather than reporting a
   * missing facility.
   */
  facilityId?: string
  /**
   * Called when the toggle switches Edit Mode _on_, so each header fork can
   * raise its own (legacy vs. rebrand) confirmation toast.
   */
  onEnterEditMode?: () => void
}

interface UseEditModeToggleI {
  isEditMode: boolean
  isConfirmOpen: boolean
  onConfirmClose: () => void
  onToggleClick: () => void
}

/**
 * Behavior behind the Floor-plan header's Edit Mode toggle, shared by the
 * legacy and rebranded forks: the Redux toggle, the first-use confirmation
 * dialog, and the preserved Mixpanel instrumentation.
 *
 * Edit Mode is being retired everywhere else (see `useIsEditModeSatisfied`),
 * but moving Devices on the Floor Plan must stay an explicit action — so this
 * reads the raw toggle rather than the flag-aware hook.
 */
export const useEditModeToggle = ({
  facilityId,
  onEnterEditMode,
}: UseEditModeToggleIProps = {}): UseEditModeToggleI => {
  const dispatch = useAppDispatch()
  // Keep as old Edit Mode check!
  const isEditMode = useIsEditMode()
  const hasConfirmedEditMode = useAppSelector(selectConfirmEditMode)
  const me = useAppSelector(selectMe)
  const {
    isOpen: isConfirmOpen,
    onOpen: onConfirmOpen,
    onClose: onConfirmClose,
  } = useDisclosure()

  const toggle = () => {
    if (!isEditMode) onEnterEditMode?.()
    dispatch(toggleEditMode())
    trackEvent(FLOOR_PLAN_EDIT_MODE_ANALYTICS.pressed, {
      facility_id: facilityId,
      role: me.roleName,
      // `entered` below can't stand in for direction — it also fires on mount
      // when Edit Mode is already on — and there is no matching exit event, so
      // the press itself has to say which way it went.
      is_entering: !isEditMode,
    })
  }

  // The very first activation goes through the confirmation dialog, which
  // enables Edit Mode itself. Every later press toggles directly.
  const onToggleClick = () =>
    hasConfirmedEditMode ? toggle() : onConfirmOpen()

  // Which facility the current activation has already been reported for.
  // Cleared when Edit Mode goes off, so the next activation reports again.
  const reportedFacilityIdRef = useRef<string>()

  // `facilityId` arrives from an async floor-details query, and Edit Mode is
  // persisted global state that can already be on when this mounts — so the
  // first pass here routinely has no facility. Hold the event until one is
  // known rather than sending `facility_id: undefined`, and let the ref keep
  // the delayed emit to one per activation. A facility change with Edit Mode
  // still on reports again: it is an entry into Edit Mode on that facility,
  // and the event is only useful broken down by facility.
  useEffect(() => {
    if (!isEditMode) {
      reportedFacilityIdRef.current = undefined
      return
    }

    if (!facilityId || reportedFacilityIdRef.current === facilityId) return

    reportedFacilityIdRef.current = facilityId
    trackEvent(FLOOR_PLAN_EDIT_MODE_ANALYTICS.entered, {
      facility_id: facilityId,
      role: me.roleName,
    })
  }, [isEditMode, facilityId])

  return { isEditMode, isConfirmOpen, onConfirmClose, onToggleClick }
}
